import { Divider, Empty, Spin } from 'antd';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as THREE from 'three';
import ButtonIconGhostMedium from '../../../../components/common/ButtonIconGhost/ButtonIconGhostMedium';
import SelectComponent from '../../../../components/common/SelectComponent/SelectComponent';
import RightMenuDesign from '../../../../components/house/RightMenuDesign/RightMenuDesign';
import { Icon } from '../../../../components/icon/icon';
import { InsideType } from '../../../../constants/enum';
import {
  fetchLegGroup,
  fetchLegs,
} from '../../../../store/actions/module.action';
import { DesignerPageContext } from '../../DesignerPage';
import CardMaterial from '../CardMaterial/CardMaterial';
import './RightLegMenu.scss';
import { useTranslation } from 'react-i18next';
import { getValueFromString } from '../../../../utils/data/getValue.utils';
import SelectionSupplier from '../SelectionSuplier/SelectionSuplier';
import { convertForeignCurrency } from '../../../../utils/currency/currency.utils';

const RightLegMenu = ({ open, onClose, title }) => {
  const { t } = useTranslation();
  const {
    selectedModels,
    selectedModel,
    sceneManager,
    product_type,
    insideLoaderRef,
    sceneRef,
    setMdSelectedModelData,
  } = useContext(DesignerPageContext);

  const [leg, setLeg] = useState();
  const [legGroupId, setLegGroupId] = useState(null);
  const { legGroup, legs } = useSelector(state => state.module);

  const dispatch = useDispatch();

  const traverseLeg = useCallback(
    (selectedModel, legGroup) => {
      const modules = sceneManager?.cabinetManager?.currentCabinet?.modules;
      if (modules && selectedModel) {
        const moduleEntries = Array.from(modules?.entries());
        const module = moduleEntries?.find(
          ([item, value]) => value.group.uuid === selectedModel?.uuid
        );

        if (module) {
          const [item, value] = module;

          const leg = value?.listInside?.find(item =>
            [InsideType.KHUNG_SAT, InsideType.CHAN_ROI_TV].includes(item.type)
          );
          if (leg) {
            setLeg({ ...leg, id: leg?._id });
          } else setLeg(null);
          if (leg?.groupId) {
            setLegGroupId(leg.groupId);
          } else if (!legGroupId) {
            setLegGroupId(legGroup?.data?.[0]?.id);
          }
        }
      }
    },
    [
      selectedModels,
      sceneManager?.cabinetManager?.currentCabinet?.modules,
      legGroup,
    ]
  );

  const handleClick = useCallback(
    async (moduleData, applyToAll) => {
      const currentCabinet = sceneManager.cabinetManager.getCurrentCabinet();

      const foundModule = currentCabinet.getModuleByGroupUuid(
        selectedModel.uuid
      );

      //Chân tủ mặc định
      if (!moduleData.model) {
        sceneManager?.autoFitModuleController.scaleModule(
          insideLoaderRef,
          selectedModel?.uuid,
          null,
          { caoChan: 0.1, caoChanRoi: 0 }
        );
        insideLoaderRef.current.scaleInsideAfterScaleChanTu(
          foundModule,
          sceneRef.current
        );
        setLeg(null);

        //Xoá chân cũ đi nếu có
        insideLoaderRef.current.removeLegModel(foundModule);

        await sceneManager.textureController.reloadTexture({
          module: foundModule,
        });
        updateMdSelectedModelData();

        return;
      }

      //Xoá chân cũ đi nếu có
      insideLoaderRef.current.removeLegModel(foundModule);

      let legHeight = 0;
      const addLegToCabinet = leg => {
        const box = new THREE.Box3().setFromObject(leg);
        const size = new THREE.Vector3();
        box.getSize(size);
        legHeight = size.y;

        const data = {
          _id: moduleData.id,
          id: moduleData.id,
          glb: moduleData.model,
          type: moduleData.type,
          gltfUuid: leg.uuid,
          gltf: leg,
          thumbnail_url: leg.userData.thumbnail_url,
          price: moduleData.price,
          name: moduleData.name,
          groupId: moduleData.group_id,
          code: moduleData?.code,
          materialType: moduleData?.material,
        };
        leg.userData.isInside = true;
        leg.userData.model = moduleData.model;

        foundModule.listInside.push(data);
        foundModule.group.add(leg);
      };

      await insideLoaderRef.current.loadLegModel(
        moduleData,
        foundModule,
        addLegToCabinet
      );

      await sceneManager?.calculateTotalCost?.({});

      //Chân tủ rời
      sceneManager?.autoFitModuleController.scaleModule(
        insideLoaderRef,
        selectedModel?.uuid,
        null,
        { caoChan: 0, caoChanRoi: legHeight }
      );
      insideLoaderRef.current.scaleInsideAfterScaleChanTu(
        foundModule,
        sceneRef.current
      );

      traverseLeg(selectedModel);

      updateMdSelectedModelData();
      setTimeout(() => {
        sceneRef.current.handleDrawBorderSelectedModule(selectedModel);
      }, 100);
    },
    [sceneManager, selectedModel, selectedModels]
  );

  const updateMdSelectedModelData = () => {
    const originalModule = sceneManager.cabinetManager.getModuleByGltfUuid(
      selectedModel?.uuid
    );
    if (originalModule) {
      setMdSelectedModelData(prevData => ({
        ...prevData,
        moduleType: originalModule.group.userData.moduleType,
        fitCeiling: originalModule.fitCeiling,
        mdDimensions: {
          ...prevData.mdDimensions,
          ...originalModule?.formularController?.currentDimensions,
        },
        mdOptions: {
          ...prevData.mdOptions,
          ...originalModule?.formularController?.currentOptions,
        },
        mdLimitedDimensions: {
          ...prevData.mdLimitedDimensions,
          ...originalModule?.moduleData?.limit,
        },
        type: null,
      }));
    }
  };

  useEffect(() => {
    if (legGroup.isNeedReloadData && open) {
      dispatch(
        fetchLegGroup({
          id: product_type,
        })
      );
    }
  }, [open, legGroup.isNeedReloadData, product_type]);

  useEffect(() => {
    if (legGroupId) {
      dispatch(fetchLegs({ id: legGroupId }));
    }
  }, [legGroupId]);

  useEffect(() => {
    if (open && legGroup.data?.length) {
      traverseLeg(selectedModel, legGroup);
    }
    // setShowDulux(false);
  }, [open, selectedModel, legGroup]);

  return (
    <>
      <RightMenuDesign
        open={open}
        classNames={{ root: 'top-84', content: 'flex flex-col !p-0 gap-0' }}
        header={
          <div className="flex self-stretch items-center justify-start gap-8">
            <ButtonIconGhostMedium
              icon={<Icon.ICArrowBack />}
              onClick={onClose}
            />
            <span className="text-title-large">{title}</span>
          </div>
        }
        content={
          <>
            <div
              key={1}
              className="flex p-16 flex-col items-start gap-8 self-stretch bg-fills-white"
            >
              {/* <SelectComponent
                options={
                  legGroup.data?.map(item => ({
                    value: item.id,
                    label: getValueFromString(item.name),
                  })) ?? []
                }
                onChange={value => {
                  setLegGroupId(value);
                }}
                value={legGroupId}
                placeholder={t('Select cabinet leg type')}
                style={{ flex: '1 0 0' }}
                size={'default'}
              /> */}
              {legGroup.data?.map(item => {
                return (
                  <SelectionSupplier
                    src={item.icon}
                    title={getValueFromString(item.name)}
                    description={getValueFromString(item.description)}
                    className={'self-stretch'}
                    onClick={() => setLegGroupId(item.id)}
                    actions={<></>}
                    active={legGroupId === item.id}
                  />
                );
              })}
            </div>
            <Divider type="horizontal" className="m-0 !p-0" />

            <div
              key={4}
              className="flex flex-col p-8 items-start gap-16 grow self-stretch overflow-auto"
              style={{ backgroundColor: '#F7F7F7' }}
            >
              <div
                id="right-palete-leg-menu"
                className="flex flex-col self-stretch grow overflow-scroll"
              >
                {legs.isLoading || legGroup.isLoading ? (
                  <div className="flex p-[24px] items-center justify-center self-stretch">
                    <Spin spinning={true} />
                  </div>
                ) : (
                  !legs.data?.length && (
                    <div className="flex items-center justify-center self-stretch grow">
                      <Empty />
                    </div>
                  )
                )}
                <div className="flex flex-row flex-wrap gap-2">
                  {legs.data?.map(legItem => {
                    return (
                      <CardMaterial
                        key={legItem.id}
                        item={{
                          title: getValueFromString(legItem.name),
                          description: (
                            <div className="flex items-start gap-[2px] justify-start flex-col">
                              {legItem.material ? (
                                <span className="text-body-small flex flex-wrap">
                                  {legItem.material}
                                </span>
                              ) : null}
                              {Number(legItem.price || 0) ? (
                                <span className="text-body-small font-semibold">
                                  {convertForeignCurrency(
                                    Number(legItem.price || 0)
                                  )}
                                </span>
                              ) : null}
                            </div>
                          ),
                          src: legItem.icon,
                        }}
                        active={
                          (leg && legItem.id === leg.id) ||
                          (!leg && legItem.model === null)
                        }
                        onClick={async (func, applyToAll) => {
                          handleClick(legItem, applyToAll);
                          func?.();
                        }}
                      />
                    );
                  })}
                </div>
              </div>
            </div>
          </>
        }
      />
    </>
  );
};

export default RightLegMenu;
